/* =============================================================================
   yojo-rail-controls.js — THE single owner of horizontal product-rail controls.

   Before this file three implementations each re-derived overflow, disabled
   state and RTL paging:
     * assets/yojo-rail.js         (.yhp-rail-section / [data-arr])   — worked
     * assets/yojo-shop.js         ([data-rail-arrow])                — click
                                    handler ONLY: no overflow test, so every
                                    homepage rail rendered two live arrows even
                                    when all its cards already fitted
     * per-section inline scripts  (retired)
   They disagreed, which is why identical-looking rails behaved differently.

   CONTRACT — a rail is any element pair inside one [data-rail-group]:

     <div data-rail-group>
       <div data-rail-nav hidden>              the whole action cluster; this
                                               file shows it only on overflow
         <button data-rail-arrow data-dir="right">
         <button data-rail-arrow data-dir="left">
       </div>
       <div data-rail-scroller> …cards… </div>
     </div>

   PHYSICAL DIRECTION CONTRACT (shared with yojo-shop.js): data-dir names what
   the customer SEES — 'right' reveals content hidden on the physical right.
   In RTL the reading order starts on the right, so scrollLeft runs 0 -> negative
   and travelling physically right DECREASES the normalised distance. One
   translation, in one place; no component may invent its own.

   State is re-derived — never cached — on: load, DOMContentLoaded, scroll,
   resize, ResizeObserver of the scroller AND the group, image load (rails size
   themselves only once art arrives), panel hydration (yojo:cards:added),
   Shopify section render (shopify:section:load) and two settling timeouts,
   because the RTL/speed app embeds re-render rail markup after load and restore
   the server-side hidden state.

   Everything is registered ONCE, globally, and delegated: per-node listeners die
   silently when those embeds replace the nodes.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__yojoRailControls) return;
  window.__yojoRailControls = 1;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  /* QA B3 (2026-08-13): the previous rAF ease-out loop wrote el.scrollLeft
     every frame. The scroller carries CSS `scroll-behavior: smooth`, so each
     of those per-frame writes started a NEW native smooth scroll toward an
     incrementally-advancing target — the two animators fought and the rail
     felt stuck. Worse, the final position (`from + 2*pitch` from an arbitrary
     start) was not a snap boundary, so `scroll-snap-type: x proximity` pulled
     the rail again after the animation ended.
     Fix: hand the whole move to the browser with ONE scrollTo() to a
     SNAP-ALIGNED target (an exact card-pitch multiple, computed in
     snapTarget below). Native smooth scrolling cooperates with scroll-snap by
     spec; reduced motion jumps instantly. */
  function animateScroll(el, from, to) {
    if (el.__yojoRailAnim) { cancelAnimationFrame(el.__yojoRailAnim); el.__yojoRailAnim = null; }
    if (REDUCE && REDUCE.matches) { el.scrollLeft = to; return; }
    el.scrollTo({ left: to, behavior: 'smooth' });
  }

  function groups(scope) {
    return (scope || document).querySelectorAll('[data-rail-group]');
  }
  function partsOf(group) {
    return {
      scroller: group.querySelector('[data-rail-scroller]'),
      nav: group.querySelector('[data-rail-nav]'),
      arrows: group.querySelectorAll('[data-rail-arrow]'),
    };
  }

  /* RTL-safe: work in a normalised, non-negative distance travelled. */
  function isRtl(el) { return getComputedStyle(el).direction === 'rtl'; }
  function pos(t) { return Math.abs(t.scrollLeft); }
  function maxScroll(t) { return Math.max(0, t.scrollWidth - t.clientWidth); }

  /* Overflow is a RENDERED-GEOMETRY question, never a product count: the number
     of visible cards changes with the breakpoint, so four cards overflow at
     390px and do not at 1440px. */
  function overflows(t) { return maxScroll(t) > 2; }

  function sync(group) {
    var p = partsOf(group);
    if (!p.scroller || !p.nav) return;
    var over = overflows(p.scroller);

    /* No overflow -> the entire cluster leaves the layout. Not "disabled
       arrows", not an empty reserved gap: nothing. */
    p.nav.hidden = !over;
    if (!over) return;

    var rtl = isRtl(p.scroller), at = pos(p.scroller), max = maxScroll(p.scroller);
    var atStart = at <= 2, atEnd = at >= max - 2;
    Array.prototype.forEach.call(p.arrows, function (b) {
      var dead = b.dataset.dir === 'right' ? (rtl ? atStart : atEnd)
                                           : (rtl ? atEnd : atStart);
      b.disabled = dead;                       /* native disabled: no click, no focus trap */
      b.setAttribute('aria-disabled', dead ? 'true' : 'false');
    });
  }

  function syncAll(scope) { groups(scope).forEach(sync); }

  /* C-10: exactly 2 cards per click, measured live so it survives resize and
     breakpoint changes. Falls back to a viewport-width step if no card is
     measurable. */
  var CARDS_PER_CLICK = 2;
  function pitchOf(scroller) {
    var card = scroller.firstElementChild;
    var st = getComputedStyle(scroller);
    var gap = parseFloat(st.columnGap || st.gap || '0') || 0;
    return card ? card.getBoundingClientRect().width + gap : 0;
  }

  /* QA B3 (2026-08-13): arrows advance to the next/previous SNAP POSITION —
     an exact card-pitch multiple — instead of `current + 2*pitch` from
     whatever mid-card offset a touch drag left behind. Everything is computed
     in the normalised non-negative distance (pos()/maxScroll() above), then
     signed once for the writing mode, so RTL (scrollLeft 0 → −max) needs no
     second mirror. Returns a signed scrollLeft value ready for scrollTo. */
  function snapTarget(scroller, dir) {
    var rtl = isRtl(scroller);
    var pitch = pitchOf(scroller);
    var max = maxScroll(scroller);
    var at = pos(scroller);
    var target;
    if (!pitch) {
      /* no measurable card: page by a viewport width, physical contract kept */
      var deltaPx = scroller.clientWidth * (dir === 'right' ? 1 : -1) * (rtl ? -1 : 1);
      target = Math.max(0, Math.min(max, at + deltaPx));
    } else {
      /* 'right' reveals content hidden on the physical right: in LTR that is
         +distance, in RTL it is −distance (reading order starts on the right). */
      var deltaN = (dir === 'right' ? 1 : -1) * (rtl ? -1 : 1);
      var idx = Math.round(at / pitch);
      target = Math.max(0, Math.min(max, (idx + deltaN * CARDS_PER_CLICK) * pitch));
    }
    return rtl ? -target : target;
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-rail-arrow]') : null;
    if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    var group = btn.closest('[data-rail-group]');
    var scroller = group && group.querySelector('[data-rail-scroller]');
    if (!scroller) return;
    /* QA B3 (2026-08-13): target is a snap-aligned position (see snapTarget),
       clamped and already signed for the writing mode. */
    var to = snapTarget(scroller, btn.dataset.dir);
    animateScroll(scroller, scroller.scrollLeft, to);
    /* Optimistic re-sync; the scroll listener corrects it when motion settles. */
    requestAnimationFrame(function () { sync(group); });
  });

  /* C-17 (2026-08-01, Motion + Mobile Spec v2) — trackpad/mousewheel
     axis-lock. Mirrors the reference Swiper config's
     mousewheel:{forceToAxis:true, sensitivity:.5, releaseOnEdges:true}:
     a vertical wheel delta over a rail redirects to horizontal scrollLeft,
     giving trackpad users natural horizontal paging. releaseOnEdges —
     once the rail is already at its start/end, let the event fall through
     un-intercepted so page scroll isn't hijacked. Disabled entirely under
     prefers-reduced-motion (REDUCE, declared above) per item 14 of the spec
     checklist — reduced-motion visitors get plain native scroll behavior. */
  /* R4 D-01 (2026-08-02) — THE WHEEL AXIS-LOCK IS DELETED.

     It redirected a vertical wheel delta over any rail into horizontal
     scrollLeft ("forceToAxis", copied from the reference's Swiper mousewheel
     config). Two things were wrong with it, and the owner's directive rules out
     the idea itself.

     1. The RTL edge logic was inverted, so it hijacked the page. In RTL
        scrollLeft runs 0 (visual start) down to -max (visual end), so with
        lo=-max and hi=0 the variable named `atEnd` (at >= hi-1) was actually
        true at the visual START. At rest a rail sits at 0: scrolling DOWN
        released correctly, but scrolling UP fell straight through to
        preventDefault() and dragged the rail sideways instead of scrolling the
        page back up. On the homepage a mouse user meets a rail roughly every
        670px, so scrolling back up over the page repeatedly stalled.

     2. Its preventDefault() fired even in cases where it then failed to move
        the rail, which — layered under the smooth-scroll wrapper that also
        listened on wheel — produced a doubled page scroll for one notch.

     The directive is explicit: do not "turn ordinary vertical gestures into
     horizontal movement", and "mouse wheel behavior must remain vertical unless
     the customer clearly performs a horizontal action". Deleting the handler
     satisfies both. Nothing is lost: the rail is a native overflow-x:auto
     scroller, so a real trackpad horizontal gesture (deltaX) still scrolls it
     natively, the arrows still page it, and touch swiping works now that
     touch-action no longer forbids it. No non-passive wheel listener remains
     on the document. */

  /* Keyboard: native <button> already handles Enter/Space. Arrow keys on the
     scroller itself are the browser's own behaviour once it is focusable. */

  document.addEventListener('scroll', function (e) {
    var t = e.target;
    if (t && t.hasAttribute && t.hasAttribute('data-rail-scroller')) {
      var g = t.closest('[data-rail-group]');
      if (g) requestAnimationFrame(function () { sync(g); });
    }
  }, true);

  window.addEventListener('resize', function () { syncAll(); });
  window.addEventListener('load', function () { syncAll(); });
  window.addEventListener('orientationchange', function () { setTimeout(syncAll, 250); });

  /* Late art changes a rail's scrollWidth long after DOMContentLoaded. */
  document.addEventListener('load', function (e) {
    var t = e.target;
    if (t && t.tagName === 'IMG') {
      var g = t.closest && t.closest('[data-rail-group]');
      if (g) sync(g);
    }
  }, true);

  /* Panels hydrated from <template> by yojo-shop.js, and any section the theme
     editor re-renders, arrive with server-side hidden arrows. */
  document.addEventListener('yojo:cards:added', function (e) {
    syncAll(e.detail && e.detail.container ? e.detail.container : document);
  });
  document.addEventListener('shopify:section:load', function (e) { syncAll(e.target); });

  if ('ResizeObserver' in window) {
    var ro = new ResizeObserver(function (entries) {
      entries.forEach(function (en) {
        var g = en.target.closest ? en.target.closest('[data-rail-group]') : null;
        if (g) sync(g);
      });
    });
    var observe = function (scope) {
      groups(scope).forEach(function (g) {
        if (g.__yojoRailObserved) return;
        g.__yojoRailObserved = 1;
        var s = g.querySelector('[data-rail-scroller]');
        /* Observe the GROUP (a layout box whose width tracks the container) and
           the scroller. Observing only the scroller misses a container resize
           that does not change the scroller's own border box. */
        ro.observe(g);
        if (s) ro.observe(s);
      });
    };
    document.addEventListener('yojo:cards:added', function (e) { observe(e.detail && e.detail.container); });
    document.addEventListener('shopify:section:load', function (e) { observe(e.target); });
    window.__yojoRailObserve = observe;
    observe();
  }

  function boot() {
    syncAll();
    if (window.__yojoRailObserve) window.__yojoRailObserve();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  /* The RTL / speed app embeds restore server HTML after load; re-derive twice. */
  setTimeout(boot, 2700);
  setTimeout(boot, 6200);

  window.YojoRailControls = { sync: sync, syncAll: syncAll };
})();
